
import { Logger } from "@/util/logger";

/**
 *
 * @class ChunkReader
 *
 * @brief iterator for producing file chunks to pass to our JSON parser.
 *
 * This code is based on the tutorial:
 *
 *   https://gchristensen.github.io/posts/readline-in-es6/
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */

export class ChunkReader {

  /** the file we are reading */
  private file: File|null = null;

  /** current position */
  private offset: number = 0;

  /** the size (bytes) of the file */
  private size = 0;

  /** the buffer size */
  private chunkSize: number = 256 * 1024;

  /** for working with binary/Unicode */
  private decoder : TextDecoder|null = null;

  /** the actual reader */
  private reader : FileReader|null = null;

  /** non-null when we are in an error state and can't go further. */
  private error : string|null = null;

  /** the tail of the last chunk, when the previous tail was inside a unicode sequence. */
  private remnantBytes: Uint8Array|null = null;

  /**
   * create a new reader
   *
   * @param file      the source JSON file
   * @param chunkSize chunk size in bytes to use (default is 256 KB)
   */
  constructor(file: File, chunkSize: number = 256*1024) {

      this.clear();

      this.file      = file;
      this.chunkSize = chunkSize;
      this.size      = file.size;

      //Logger.debug(`[ChunkReader] ready to read! file=${this.file.name}, sz=${this.file.size}`);
  }

  /** fetch the error message, if there is one */
  public errorMessage(): string|null {

    return this.error;
  }

  /** check if we have an error */
  public hasError(): boolean {

    return null !== this.error;
  }

  /** check if we are done reading the whole file */
  public complete(): boolean {

    return this.offset >= this.size;
  }

  public clear(): void {
    this.file         = null;
    this.offset       = 0;
    this.size         = 0;
    this.decoder      = new TextDecoder();
    this.reader       = new FileReader();
    this.chunkSize    = 256*1024;
    this.error        = null;
    this.remnantBytes = null;
  }

  /** check for end of file (all input has been read */
  public eof(): boolean {

    return this.offset >= this.size;
  }

  /** fetch the current offset */
  public tell(): number {

    return this.offset;
  }

  /** fetch tne next chunk */
  public chunk(): Promise<string|null> {

    return new Promise((resolve, reject) => {

      if (this.eof()) {

        /* resolve the promised output with an empty string/buffer */

        resolve(null);
      }

      if (this.error) {

        /* reject with the current error */

        Logger.error(`[ChunkReader] ${this.error}`);

        reject(this.error);
      }

      if (!this.reader) {

        /* reject with the current error */

        Logger.error(`[ChunkReader] no reader available.`);

        reject(`[ChunkReader] no reader available.`);

        return ;
      }

      if (!this.decoder) {

        /* reject with the current error */

        Logger.error(`[ChunkReader] no decoder available.`);

        reject(`[ChunkReader] no decoder available.`);

        return ;
      }

      if (!this.file) {

        /* reject with the current error */

        Logger.error(`[ChunkReader] no file available.`);

        reject(`[ChunkReader] no file available.`);

        return ;
      }

      this.reader.onloadend = () => {

        /* advance further into the file */

        this.offset += this.chunkSize;

        /* process this chunk ... */

        // @ts-ignore
        let chunk: ArrayBuffer = this.reader.result;

        let bytes: Uint8Array  = new Uint8Array(chunk);
        let point: number      = bytes.length - 1;
        let split: boolean     = false;

        let remnant: Uint8Array;

        /*
         * if the last byte is in the middle of a unicode sequence...rewind a bit
         * so that our buffer is always cleanly aligned.
         *
         */

        if ((bytes[point] & 0b11000000) === 0b11000000) {

          split = true;

        } else {

          while (point && (bytes[point] & 0b11000000) === 0b10000000) {

            point -= 1;
          }

          if (point !== bytes.length - 1) {

            split = true;
          }
        }

        if (split) {

          /*
           * we had to rewind the end of the buffer to align to the end
           * of a normal byte sequence and the start of a unicode sequence,
           * so now we have to save the leftover part, to use next time.
           *
           */

          remnant = bytes.slice(point);
          bytes   = bytes.slice(0, point);

          /*
           * if we have left over bytes (partial unicode sequence) from the last
           * chunk, then prepend it into this current chunk.  The current chunk may
           * or may not have its own trailing partial unicode sequence.
           *
           */

          if (this.remnantBytes) {

            let newBytes: Uint8Array = new Uint8Array(this.remnantBytes.length + bytes.length);

            newBytes.set(this.remnantBytes);
            newBytes.set(bytes, this.remnantBytes.length);

            bytes = newBytes;
          }

          /* save the partial unicode sequence for the next buffer */

          this.remnantBytes = remnant;

        } else {

          /*
           * even if this current chunk doesn't have a partial unicode sequence
           * at the end of the chunk...we may still have had one in the previous
           * chunk.  So be sure to prepend it...if needed.
           *
           */

          if (this.remnantBytes) {

            let newBytes = new Uint8Array(this.remnantBytes.length + bytes.length);

            newBytes.set(this.remnantBytes);
            newBytes.set(bytes, this.remnantBytes.length);

            bytes = newBytes;
          }

          /* previous trailing sequence has now been consumed */

          this.remnantBytes = null;
        }

        /* resolve the promised output */

        // @ts-ignore
        let output: string = this.decoder.decode(bytes);

        resolve(output);

      };

      this.reader.onerror = (e: ProgressEvent<FileReader>) => {

        this.error = `Could not read chunk from file @ ${this.offset} of ${this.size} total bytes.`;

        Logger.error(`[ChunkReader] `, e);

        reject(e);
      };

      /* trigger the read from the file ... */

      let b = this.file.slice(this.offset, this.offset + this.chunkSize);

      this.reader.readAsArrayBuffer(b);

    }); /* end of promise */

  }

}


